import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const steps = [
  {
    n: "01",
    title: "The Offer",
    body: "You submit an offer to purchase, typically subject to court approval. Because the property is sold through the court, terms are firmer than a standard sale — deposits and conditions are structured accordingly.",
  },
  {
    n: "02",
    title: "Court Approval",
    body: "The accepted offer is presented to the Supreme Court of British Columbia. On the approval date, other interested buyers may attend and submit competing offers in open court. The judge approves the highest and best offer.",
  },
  {
    n: "03",
    title: "Completion",
    body: "Once the court grants approval, the sale proceeds to completion much like a conventional transaction — funds transfer, title changes hands, and you take possession. Mike coordinates with lawyers and lenders throughout.",
  },
];

export default function Process() {
  return (
    <div data-testid="process-page">
      <Seo
        title="The Court-Ordered Sale Process for Buyers | Mike Sidhu, Personal Real Estate Corporation"
        description="Understand how court-ordered and foreclosure sales work in BC — from your offer to Supreme Court approval to completion — guided by Mike Sidhu, Personal Real Estate Corporation."
        path="/process"
      />
      <section className="bg-navy pt-36 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><p className="overline">For Buyers</p></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-white mt-4 leading-tight">The Process</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-white/70 text-lg mt-6 max-w-2xl leading-relaxed">
              How court-ordered sales work for buyers — from your first offer to the keys in your hand.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="relative">
            <div className="absolute left-[27px] top-2 bottom-2 w-px bg-navy/15 hidden sm:block" />
            <div className="space-y-14">
              {steps.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.1}>
                  <div className="flex gap-6 sm:gap-8">
                    <div className="shrink-0">
                      <div className="w-14 h-14 border border-brass text-brass font-mono flex items-center justify-center bg-paper relative z-10">
                        {s.n}
                      </div>
                    </div>
                    <div className="pt-1">
                      <h3 className="font-heading text-2xl md:text-3xl text-navy">{s.title}</h3>
                      <p className="font-body text-ink-soft text-lg leading-relaxed mt-3">{s.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-20 bg-navy text-white p-10 md:p-12">
              <p className="overline mb-4">A Note on Guidance</p>
              <p className="font-body text-white/85 text-lg leading-relaxed">
                Court-ordered purchases carry unique risks and timelines. Mike Sidhu, Personal Real Estate Corporation, walks
                you through inspections, financing, deposit structure and the approval hearing so you can
                bid with confidence — never blind.
              </p>
              <Link to="/contact" data-testid="process-cta-contact" className="inline-flex items-center gap-2 mt-8 bg-brass text-white px-7 py-3.5 hover:bg-white hover:text-navy transition-colors duration-300">
                Ask Mike a Question <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
